import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '@/lib/api';
import { Plus, Pencil, Trash2, Upload, Search, RotateCcw } from 'lucide-react';

const emptyProduct = { article_number: '', name: '', category_id: 'none', price: 0, purchase_price: 0, supplier_id: '', description: '', image_url: '', sort_order: 0, status: 'active', stock_note: '', internal_note: '', manual_purchase_count: 0 };

// German umlaut normalization for sorting
const normalizeGerman = (text) => {
  if (!text) return "";
  const replacements = [['ä', 'ae'], ['Ä', 'Ae'], ['ö', 'oe'], ['Ö', 'Oe'], ['ü', 'ue'], ['Ü', 'Ue'], ['ß', 'ss']];
  let result = text.toLowerCase();
  replacements.forEach(([old, newChar]) => { result = result.split(old).join(newChar); });
  return result;
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  
  // Filter state
  const [filterArticle, setFilterArticle] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const load = useCallback(() => {
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sorted categories for dropdown (alphabetically, only active)
  const sortedCategories = useMemo(() => {
    return [...categories]
      .filter(c => c.active !== false)
      .sort((a, b) => normalizeGerman(a.name || '').localeCompare(normalizeGerman(b.name || '')));
  }, [categories]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      // Article number filter (partial match, case-insensitive)
      if (filterArticle && !p.article_number.toLowerCase().includes(filterArticle.toLowerCase())) {
        return false;
      }
      // Name filter (partial match, case-insensitive)
      if (filterName && !p.name.toLowerCase().includes(filterName.toLowerCase())) {
        return false;
      }
      // Category filter
      if (filterCategory && filterCategory !== 'all' && p.category_id !== filterCategory) {
        return false;
      }
      return true;
    });
  }, [products, filterArticle, filterName, filterCategory]);

  // Check if any filter is active
  const hasActiveFilters = filterArticle || filterName || (filterCategory && filterCategory !== 'all');

  // Reset all filters
  const resetFilters = () => {
    setFilterArticle('');
    setFilterName('');
    setFilterCategory('all');
  };

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/media/upload', fd);
      handleChange('image_url', res.data.url);
      toast.success('Bild hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.article_number || !form.name) { toast.error('Artikelnummer und Name sind Pflichtfelder'); return; }
    try {
      const payload = { ...form, category_id: form.category_id === 'none' ? '' : form.category_id };
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success('Produkt aktualisiert');
      } else {
        await api.post('/products', payload);
        toast.success('Produkt erstellt');
      }
      setForm(emptyProduct);
      setEditing(null);
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleEdit = (p) => {
    setForm({ article_number: p.article_number, name: p.name, category_id: p.category_id || 'none', price: p.price, purchase_price: p.purchase_price || 0, supplier_id: p.supplier_id || '', description: p.description || '', image_url: p.image_url || '', sort_order: p.sort_order || 0, status: p.status || 'active', stock_note: p.stock_note || '', internal_note: p.internal_note || '', manual_purchase_count: p.manual_purchase_count || 0 });
    setEditing(p.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    try { await api.delete(`/products/${id}`); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  const catName = (catId) => categories.find(c => c.id === catId)?.name || '-';
  const imgUrl = (url) => url ? (url.startsWith('/api') ? `${BACKEND_URL}${url}` : url) : '';

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Produkte</h1>
        <Button onClick={() => { setForm(emptyProduct); setEditing(null); setDialogOpen(true); }} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-1" /> Neues Produkt
        </Button>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-sm shadow-sm border border-slate-100 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Article Number Filter */}
          <div className="flex-1 min-w-[140px] max-w-[180px]">
            <Label className="text-xs text-slate-500 mb-1 block">Art.Nr.</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Suchen..."
                value={filterArticle}
                onChange={e => setFilterArticle(e.target.value)}
                className="pl-8 h-9 rounded-sm text-sm"
                data-testid="filter-article"
              />
            </div>
          </div>

          {/* Name Filter */}
          <div className="flex-1 min-w-[180px] max-w-[250px]">
            <Label className="text-xs text-slate-500 mb-1 block">Name</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="z.B. Tomate..."
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                className="pl-8 h-9 rounded-sm text-sm"
                data-testid="filter-name"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex-1 min-w-[180px] max-w-[220px]">
            <Label className="text-xs text-slate-500 mb-1 block">Kategorie</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="filter-category">
                <SelectValue placeholder="Alle Kategorien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {sortedCategories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reset Button */}
          <div className="flex items-center">
            <Button
              variant="outline"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className="h-9 rounded-sm text-xs"
              data-testid="filter-reset"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Zurücksetzen
            </Button>
          </div>
        </div>

        {/* Filter Info */}
        {hasActiveFilters && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              <span className="font-medium text-[#0E518D]">{filteredProducts.length}</span> von {products.length} Produkten
              {filterArticle && <span className="ml-2">• Art.Nr. enthält „{filterArticle}"</span>}
              {filterName && <span className="ml-2">• Name enthält „{filterName}"</span>}
              {filterCategory && filterCategory !== 'all' && <span className="ml-2">• Kategorie: {categories.find(c => c.id === filterCategory)?.name}</span>}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wide">Bild</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Art.Nr.</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Kategorie</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Preis</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">EK netto</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Best.</TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">
                {hasActiveFilters ? 'Keine Produkte gefunden' : 'Keine Produkte vorhanden'}
              </TableCell></TableRow>
            ) : filteredProducts.map(p => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell>{p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-10 h-10 object-cover rounded-sm" /> : <div className="w-10 h-10 bg-slate-100 rounded-sm" />}</TableCell>
                <TableCell className="text-sm font-mono">{p.article_number}</TableCell>
                <TableCell className="text-sm font-medium">{p.name}</TableCell>
                <TableCell className="text-sm">{catName(p.category_id)}</TableCell>
                <TableCell className="text-sm font-bold text-[#0E518D]">{p.price?.toFixed(2)} EUR</TableCell>
                <TableCell className="text-sm text-slate-500">{(p.purchase_price || 0).toFixed(2)} EUR</TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.status === 'active' ? 'border-green-500 text-green-600 text-xs' : 'border-slate-300 text-slate-500 text-xs'}>
                    {p.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-center">{(p.auto_order_count || 0) + (p.manual_purchase_count || 0)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E518D]">{editing ? 'Produkt bearbeiten' : 'Neues Produkt'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-sm">Artikelnummer *</Label>
              <Input value={form.article_number} onChange={e => handleChange('article_number', e.target.value)} className="mt-1 rounded-sm" data-testid="product-article-number" />
            </div>
            <div>
              <Label className="text-sm">Produktname *</Label>
              <Input value={form.name} onChange={e => handleChange('name', e.target.value)} className="mt-1 rounded-sm" data-testid="product-name" />
            </div>
            <div>
              <Label className="text-sm">Kategorie</Label>
              <select
                value={form.category_id}
                onChange={e => handleChange('category_id', e.target.value)}
                className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="product-category"
              >
                <option value="none">Keine</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm">Preis (EUR) - Verkaufspreis</Label>
              <Input type="number" step="0.01" value={form.price} onChange={e => handleChange('price', parseFloat(e.target.value) || 0)} className="mt-1 rounded-sm" data-testid="product-price" />
            </div>
            <div>
              <Label className="text-sm">Einkaufspreis (EUR) - intern</Label>
              <Input type="number" step="0.01" value={form.purchase_price} onChange={e => handleChange('purchase_price', parseFloat(e.target.value) || 0)} className="mt-1 rounded-sm" data-testid="product-purchase-price" />
              <p className="text-xs text-slate-400 mt-1">Nur für interne Gewinnberechnung sichtbar</p>
            </div>
            <div>
              <Label className="text-sm">Lieferant</Label>
              <Select value={form.supplier_id || 'none'} onValueChange={v => handleChange('supplier_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="product-supplier">
                  <SelectValue placeholder="Kein Lieferant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Lieferant</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Für Lieferanten-Benachrichtigung</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Beschreibung</Label>
              <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={3} className="mt-1 rounded-sm" data-testid="product-description" />
            </div>
            <div>
              <Label className="text-sm">Produktbild</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="file" accept="image/*" onChange={handleImageUpload} className="rounded-sm text-xs" disabled={uploading} data-testid="product-image-upload" />
              </div>
              {form.image_url && <img src={imgUrl(form.image_url)} alt="" className="mt-2 w-20 h-20 object-cover rounded-sm" />}
            </div>
            <div>
              <Label className="text-sm">Sortierung</Label>
              <Input type="number" value={form.sort_order} onChange={e => handleChange('sort_order', parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" data-testid="product-sort-order" />
            </div>
            <div>
              <Label className="text-sm">Status</Label>
              <select
                value={form.status}
                onChange={e => handleChange('status', e.target.value)}
                className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="product-status"
              >
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Kaufanzahl (manuell)</Label>
              <Input type="number" value={form.manual_purchase_count} onChange={e => handleChange('manual_purchase_count', parseInt(e.target.value) || 0)} className="mt-1 rounded-sm" data-testid="product-manual-count" />
            </div>
            <div>
              <Label className="text-sm">Lagerhinweis</Label>
              <Input value={form.stock_note} onChange={e => handleChange('stock_note', e.target.value)} className="mt-1 rounded-sm" data-testid="product-stock-note" />
            </div>
            <div>
              <Label className="text-sm">Interne Notiz</Label>
              <Input value={form.internal_note} onChange={e => handleChange('internal_note', e.target.value)} className="mt-1 rounded-sm" data-testid="product-internal-note" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm uppercase tracking-wide text-xs" data-testid="product-cancel">Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="product-save">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
